import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';
import { OverlayPanel } from '../ui/OverlayPanel';

// ============ TYPES ============

interface CourierContract {
  id: string;
  originLocationId: string;
  originLocationName: string;
  originX: number;
  originY: number;
  destinationLocationId: string;
  destinationLocationName: string;
  destinationX: number;
  destinationY: number;
  contractType: 'standard' | 'regulated';
  packageName: string;
  packageDescription: string;
  packageRarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  packageIcon?: string;
  rewardPieces: number;
  rewardXp: number;
  rewardReputation: number;
  timeLimitMinutes: number | null;
  expiresAt: number | null;
  status: string;
}

interface CourierPackage {
  id: string;
  contractId: string;
  itemId: string;
  name: string;
  description: string;
  rarity: string;
  destinationLocationId: string;
  destinationLocationName: string;
  destinationX: number;
  destinationY: number;
  rewardPieces: number;
  timeLimitExpiresAt: number | null;
  status: string;
}

interface CourierStatistics {
  totalDeliveries: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  stolenPackages: number;
  packagesLostToPvp: number;
  totalPiecesEarned: number;
  currentStreak: number;
  bestStreak: number;
}

interface CourierPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  locationName: string;
  locationType: 'trading_post' | 'harbor_office';
  onSetDestination?: (x: number, y: number, name: string) => void;
}

// ============ RARITY COLORS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

// ============ COMPONENT ============

export const CourierPanel: React.FC<CourierPanelProps> = ({
  socket,
  isOpen,
  onClose,
  locationId,
  locationName,
  locationType,
  onSetDestination,
}) => {
  const { confirm } = useConfirm();
  const [contracts, setContracts] = useState<CourierContract[]>([]);
  const [activeContracts, setActiveContracts] = useState<CourierContract[]>([]);
  const [activePackages, setActivePackages] = useState<CourierPackage[]>([]);
  const [stats, setStats] = useState<CourierStatistics | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'active' | 'stats'>('available');
  const [selectedContract, setSelectedContract] = useState<CourierContract | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Keyboard handler for closing with Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Request data on open
  useEffect(() => {
    if (isOpen && socket) {
      setIsLoading(true);
      socket.emit('courier:getContracts', { locationId });
      socket.emit('courier:getActiveContracts');
      socket.emit('courier:getStats');
    }
  }, [isOpen, socket, locationId]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleContracts = (data: { locationId: string; contracts: CourierContract[] }): void => {
      if (data.locationId === locationId) {
        setContracts(data.contracts);
        setIsLoading(false);
      }
    };

    const handleActiveContracts = (data: { contracts: CourierContract[]; packages?: CourierPackage[] }): void => {
      setActiveContracts(data.contracts);
      setActivePackages(data.packages ?? []);
    };

    const handleStats = (data: { stats: CourierStatistics | null }): void => {
      setStats(data.stats);
    };

    const handleAccepted = (data: { contract: CourierContract; message: string }): void => {
      setMessage(data.message);
      setSelectedContract(null);
      // Refresh contracts
      socket.emit('courier:getContracts', { locationId });
      socket.emit('courier:getActiveContracts');
      setTimeout(() => setMessage(''), 3000);
    };

    const handleDelivered = (data: { message: string; reward: { pieces: number } }): void => {
      setMessage(`${data.message}`);
      socket.emit('courier:getActiveContracts');
      socket.emit('courier:getStats');
      setTimeout(() => setMessage(''), 5000);
    };

    const handleAbandoned = (data: { message: string }): void => {
      setMessage(data.message);
      socket.emit('courier:getActiveContracts');
      setTimeout(() => setMessage(''), 3000);
    };

    const handleError = (data: { message: string }): void => {
      setMessage(`Error: ${data.message}`);
      setTimeout(() => setMessage(''), 3000);
    };

    socket.on('courier:contracts', handleContracts);
    socket.on('courier:activeContracts', handleActiveContracts);
    socket.on('courier:stats', handleStats);
    socket.on('courier:accepted', handleAccepted);
    socket.on('courier:delivered', handleDelivered);
    socket.on('courier:abandoned', handleAbandoned);
    socket.on('courier:error', handleError);

    return () => {
      socket.off('courier:contracts', handleContracts);
      socket.off('courier:activeContracts', handleActiveContracts);
      socket.off('courier:stats', handleStats);
      socket.off('courier:accepted', handleAccepted);
      socket.off('courier:delivered', handleDelivered);
      socket.off('courier:abandoned', handleAbandoned);
      socket.off('courier:error', handleError);
    };
  }, [socket, locationId]);

  const handleAcceptContract = useCallback((contractId: string): void => {
    socket?.emit('courier:accept', { contractId });
  }, [socket]);

  const handleDeliverPackage = useCallback((packageId: string): void => {
    socket?.emit('courier:deliver', { packageId, locationId });
  }, [socket, locationId]);

  const handleAbandonContract = useCallback((contractId: string): void => {
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Abandon Contract',
        message: 'Are you sure you want to abandon this contract? You will lose the package.',
        confirmText: 'Abandon',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket?.emit('courier:abandon', { contractId });
    })();
  }, [socket, confirm]);

  const handleSetDestination = useCallback((x: number, y: number, name: string): void => {
    if (onSetDestination) {
      onSetDestination(x, y, name);
      onClose();
    }
  }, [onSetDestination, onClose]);

  const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.round(Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)) / 16); // Divide by TILE_SIZE=16
  };

  const formatTimeLimit = (minutes: number | null): string => {
    if (!minutes) return 'No limit';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTimeRemaining = (expiresAt: number | null): string => {
    if (!expiresAt) return 'No limit';
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return 'EXPIRED';
    const minutes = Math.floor(remaining / 60000);
    if (minutes < 60) return `${minutes}m remaining`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m remaining`;
  };

  // Check if any active package can be delivered at this location
  const getDeliverablePackage = (): CourierPackage | null => {
    return activePackages.find((pkg) => pkg.destinationLocationId === locationId) || null;
  };

  if (!isOpen) return null;

  const deliverablePackage = getDeliverablePackage();
  
  // Limit contracts to 3 per board
  const limitedContracts = contracts.slice(0, 3);

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title={locationType === 'harbor_office' ? 'HARBOR OFFICE COURIER' : 'COURIER BOARD'}
      draggable
      storageKey="courierPanelPosition"
      defaultPosition={{ x: 200, y: 150 }}
      closeOnBackdrop={false}
      width={720}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          fontFamily: 'var(--font-mono)',
          color: '#e6edf3',
        }}
      >
        {/* Location subtitle */}
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #21262d',
            color: '#8b949e',
            fontSize: '11px',
            textAlign: 'center',
          }}
        >
          {locationName}
        </div>

        {/* Deliverable Package Alert */}
        {deliverablePackage && (
          <div
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #3fb950',
              margin: '10px',
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ color: '#3fb950', fontWeight: 700, fontSize: '13px' }}>
                DELIVERY READY
              </div>
              <div style={{ color: '#c9d1d9', fontSize: '12px' }}>
                {deliverablePackage.name} — Reward: {deliverablePackage.rewardPieces.toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => handleDeliverPackage(deliverablePackage.id)}
              style={{
                backgroundColor: '#3fb950',
                border: 'none',
                color: '#0b1021',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              DELIVER
            </button>
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #21262d',
          }}
        >
          {(['available', 'active', 'stats'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: activeTab === tab ? '#111827' : '#0d1117',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #3fb950' : '2px solid transparent',
                color: activeTab === tab ? '#e5e7eb' : '#8b949e',
                cursor: 'pointer',
                textTransform: 'uppercase',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {tab === 'available' && `Available (${contracts.length})`}
              {tab === 'active' && `Active (${activeContracts.length})`}
              {tab === 'stats' && 'Statistics'}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '8px 16px',
              backgroundColor: message.startsWith('Error') ? '#2f1313' : '#0f2e1d',
              color: message.startsWith('Error') ? '#f85149' : '#3fb950',
              fontSize: '12px',
              textAlign: 'center',
            }}
          >
            {message}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '12px',
            backgroundColor: '#0d1117',
          }}
        >
          {isLoading ? (
            <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px' }}>
              Loading contracts...
            </div>
          ) : activeTab === 'available' ? (
            contracts.length === 0 ? (
              <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px' }}>
                No contracts available at this location.
                <br />
                <span style={{ fontSize: '12px' }}>Check back later or try another location.</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {limitedContracts.map((contract) => (
                  <div
                    key={contract.id}
                    style={{
                      backgroundColor: selectedContract?.id === contract.id ? '#111827' : '#0f172a',
                      border: `2px solid ${RARITY_COLORS[contract.packageRarity]}`,
                      padding: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => setSelectedContract(contract)}
                  >
                    {/* Left side icon for package */}
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        minWidth: '48px',
                        backgroundColor: '#161b22',
                        border: `2px solid ${RARITY_COLORS[contract.packageRarity]}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#0d1117',
                        overflow: 'hidden',
                      }}
                    >
                      {contract.packageIcon ? (
                        <img
                          src={contract.packageIcon.startsWith('/') ? `${process.env.PUBLIC_URL || ''}${contract.packageIcon}` : contract.packageIcon}
                          alt={contract.packageName}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            (target.parentElement as HTMLElement).textContent = contract.contractType === 'regulated' ? '[R]' : '[P]';
                          }}
                        />
                      ) : (
                        contract.contractType === 'regulated' ? '[R]' : '[P]'
                      )}
                    </div>

                    {/* Main content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ color: RARITY_COLORS[contract.packageRarity], fontWeight: 700, fontSize: '14px' }}>
                            {contract.packageName}
                          </div>
                          <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '2px' }}>
                            {RARITY_LABELS[contract.packageRarity]} {contract.contractType === 'regulated' ? 'Regulated' : 'Standard'} Contract
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: '14px' }}>
                            {contract.rewardPieces.toLocaleString()}₱
                          </div>
                          {contract.rewardXp > 0 && (
                            <div style={{ color: '#60a5fa', fontSize: '11px' }}>
                              +{contract.rewardXp} XP
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ color: '#c9d1d9', fontSize: '12px', marginTop: '8px' }}>
                        {contract.packageDescription}
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px', fontSize: '11px' }}>
                        <div style={{ color: '#8b949e' }}>
                          <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Destination</div>
                          <span style={{ color: '#58a6ff' }}>{contract.destinationLocationName}</span>
                        </div>
                        <div style={{ color: '#8b949e' }}>
                          <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Distance</div>
                          {calculateDistance(contract.originX, contract.originY, contract.destinationX, contract.destinationY)} t
                        </div>
                        <div style={{ color: contract.timeLimitMinutes ? '#fbbf24' : '#8b949e' }}>
                          <div style={{ color: '#666', fontSize: '9px', textTransform: 'uppercase', marginBottom: '2px' }}>Time Limit</div>
                          {formatTimeLimit(contract.timeLimitMinutes)}
                        </div>
                      </div>

                      {selectedContract?.id === contract.id && (
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptContract(contract.id);
                            }}
                            style={{
                              flex: 1,
                              backgroundColor: '#3fb950',
                              border: 'none',
                              color: '#0b1021',
                              padding: '8px',
                              cursor: 'pointer',
                              fontWeight: 700,
                            }}
                          >
                            ACCEPT CONTRACT
                          </button>
                          {onSetDestination && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDestination(contract.destinationX, contract.destinationY, contract.destinationLocationName);
                              }}
                              style={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #30363d',
                                color: '#e5e7eb',
                                padding: '8px 12px',
                                cursor: 'pointer',
                              }}
                            >
                              SET DEST
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {contracts.length > 3 && (
                  <div style={{ color: '#8b949e', fontSize: '11px', textAlign: 'center', padding: '8px', fontStyle: 'italic' }}>
                    Showing 3 of {contracts.length} available contracts
                  </div>
                )}
              </div>
            )
          ) : activeTab === 'active' ? (
            activeContracts.length === 0 ? (
              <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                No active contracts.
                <br />
                <span style={{ fontSize: '12px' }}>Accept a contract from the Available tab.</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activeContracts.map((contract) => {
                  const pkg = activePackages.find((p) => p.contractId === contract.id);
                  const isDeliverable = pkg?.destinationLocationId === locationId;
                  
                  return (
                    <div
                      key={contract.id}
                      style={{
                        backgroundColor: isDeliverable ? '#2a4a2a' : '#2a2a3a',
                        border: `2px solid ${isDeliverable ? '#4a8a4a' : RARITY_COLORS[contract.packageRarity]}`,
                        padding: '12px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ color: RARITY_COLORS[contract.packageRarity], fontWeight: 'bold', fontSize: '14px' }}>
                            {contract.packageName}
                          </div>
                          <div style={{ color: '#888', fontSize: '11px', marginTop: '2px' }}>
                            {pkg ? 'In Transit' : 'Accepted'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '14px' }}>
                            {contract.rewardPieces.toLocaleString()}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px' }}>
                        <div style={{ color: '#888' }}>
                          Deliver to: <span style={{ color: '#aaf' }}>{contract.destinationLocationName}</span>
                        </div>
                        {pkg?.timeLimitExpiresAt && (
                          <div style={{ color: pkg.timeLimitExpiresAt - Date.now() < 300000 ? '#f88' : '#fa8' }}>
                            {getTimeRemaining(pkg.timeLimitExpiresAt)}
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                        {isDeliverable && pkg && (
                          <button
                            onClick={() => handleDeliverPackage(pkg.id)}
                            style={{
                              flex: 1,
                              backgroundColor: '#4a8a4a',
                              border: 'none',
                              color: '#fff',
                              padding: '8px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                            }}
                          >
                            DELIVER NOW
                          </button>
                        )}
                        {onSetDestination && !isDeliverable && (
                          <button
                            onClick={() => handleSetDestination(contract.destinationX, contract.destinationY, contract.destinationLocationName)}
                            style={{
                              flex: 1,
                              backgroundColor: '#1f2937',
                              border: 'none',
                              color: '#e5e7eb',
                              padding: '8px',
                              cursor: 'pointer',
                            }}
                          >
                            SET DESTINATION
                          </button>
                        )}
                        <button
                          onClick={() => handleAbandonContract(contract.id)}
                          style={{
                            backgroundColor: '#6a4a4a',
                            border: 'none',
                            color: '#fff',
                            padding: '8px 12px',
                            cursor: 'pointer',
                          }}
                        >
                          ABANDON
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // Stats tab
            <div style={{ padding: '16px' }}>
              {stats ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Total Deliveries</div>
                    <div style={{ color: '#e5e7eb', fontSize: '24px', fontWeight: 700 }}>{stats.totalDeliveries}</div>
                  </div>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Success Rate</div>
                    <div style={{ color: '#3fb950', fontSize: '24px', fontWeight: 700 }}>
                      {stats.totalDeliveries > 0 
                        ? Math.round((stats.successfulDeliveries / stats.totalDeliveries) * 100) 
                        : 0}%
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Total Earned</div>
                    <div style={{ color: '#f59e0b', fontSize: '24px', fontWeight: 700 }}>
                      {stats.totalPiecesEarned.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Current Streak</div>
                    <div style={{ color: '#60a5fa', fontSize: '24px', fontWeight: 700 }}>{stats.currentStreak}</div>
                    <div style={{ color: '#6b7280', fontSize: '10px' }}>Best: {stats.bestStreak}</div>
                  </div>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Packages Stolen</div>
                    <div style={{ color: '#f85149', fontSize: '24px', fontWeight: 700 }}>{stats.stolenPackages}</div>
                  </div>
                  <div style={{ backgroundColor: '#0f172a', padding: '16px', border: '1px solid #21262d' }}>
                    <div style={{ color: '#8b949e', fontSize: '11px', textTransform: 'uppercase' }}>Lost to PvP</div>
                    <div style={{ color: '#fbbf24', fontSize: '24px', fontWeight: 700 }}>{stats.packagesLostToPvp}</div>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#8b949e', textAlign: 'center', padding: '40px' }}>
                  No courier statistics yet.
                  <br />
                  <span style={{ fontSize: '12px' }}>Complete your first delivery to start tracking!</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </OverlayPanel>
  );
};

export default CourierPanel;
